import useWebsiteBuilder, { ExternalLinksStyle, WebsiteBuilderContent, WebsiteBuilderStyle } from 'Hooks/useWebsiteBuilder';
import { makeKey, styx } from 'modules/Utils';
import { CSSProperties } from 'react';
import Icon from 'elements/Icon';
import { whiteOrBlackTextColor } from 'helpers/StyleUtils';

interface ExternalLinksPreviewContentProps {
    contentIndex: number;
    websiteContent: WebsiteBuilderContent[];
    websiteStyle: WebsiteBuilderStyle;
    style?: CSSProperties;
    onClickContentBlock?: (index: number) => void;
}

const ExternalLinksPreviewContent = ({
    contentIndex,
    websiteContent: websiteContentProp,
    websiteStyle: websiteStyleProp,
    style: styleProp,
    onClickContentBlock,
}: ExternalLinksPreviewContentProps) => {
    const {
        websiteContent: websiteContentState,
        websiteStyle: websiteStyleState,
    } = useWebsiteBuilder();

    const websiteContent = websiteContentProp || websiteContentState;
    const websiteStyle = websiteStyleProp || websiteStyleState;

    const {
        textColor: OLD,
        backgroundColor: backgroundColorWebsiteStyle,
        secondaryColor: secondaryColorWebsiteStyle,
        fontFamily,
    } = websiteStyle;

    const externalLinksContent = websiteContent[contentIndex].externalLinksContent;

    const isContentBackgroundColorEnabled = websiteContent[contentIndex].isContentBackgroundColorEnabled;
    const contentBackgroundColor = websiteContent[contentIndex].contentBackgroundColor;
    const backgroundColor = isContentBackgroundColorEnabled ? contentBackgroundColor || backgroundColorWebsiteStyle : backgroundColorWebsiteStyle;

    const isContentTextColorEnabled = websiteContent[contentIndex].isContentTextColorEnabled;
    const contentTextColor = websiteContent[contentIndex].contentTextColor;

    const textColor = isContentTextColorEnabled ? contentTextColor : whiteOrBlackTextColor(backgroundColor);

    const isContentSecondaryColorEnabled = websiteContent[contentIndex].isContentSecondaryColorEnabled;
    const contentSecondaryColor = websiteContent[contentIndex].contentSecondaryColor;
    const secondaryColorLocal = isContentSecondaryColorEnabled ? contentSecondaryColor || secondaryColorWebsiteStyle : secondaryColorWebsiteStyle;

    const getFlexDirection = (style: ExternalLinksStyle | undefined) => {
        if (!style) {
            return 'row';
        }

        switch (style) {
            case ExternalLinksStyle.BOXES:
                return 'row';
            default:
                return 'column';
        }
    }

    // NOTE: render()
    return (
        <div
            className="externalLinksPreviewContentComponent"
            style={styx(
                sty.externalLinksPreviewContentComponent,
                {
                    flexDirection: getFlexDirection(externalLinksContent?.style),
                },
                styleProp,
            )}
            onClick={() => onClickContentBlock?.(contentIndex)}
        >
            {externalLinksContent?.platforms.map((platform, index) => {
                if (!platform.isEnabled) {
                    return null;
                }

                switch (externalLinksContent.style) {
                    case ExternalLinksStyle.HYPERLINKS:
                        return (
                            <div
                                key={makeKey(platform.name, index, contentIndex)}
                                className="externalLinksPreviewContentPlatform"
                                style={styx(
                                    sty.externalLinksPreviewContentPlatform,
                                    {
                                        borderColor: `${textColor}30`,
                                    }
                                )}
                            >
                                <Icon
                                    className="externalLinksPreviewContentIcon"
                                    icon="link"
                                    size={24}
                                    color={textColor}
                                />
                                <div className="externalLinksPreviewContentTitleContainer">
                                    <div
                                        className="externalLinksPreviewContentTitle"
                                        style={{
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            whiteSpace: 'normal',
                                            color: textColor,
                                        }}
                                    >
                                        {platform.title}
                                    </div>
                                    <div
                                        className="externalLinksPreviewContentLink"
                                        style={{
                                            fontSize: 12,
                                            color: textColor,
                                            opacity: 0.5,
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            whiteSpace: 'normal',
                                        }}
                                    >
                                        {platform.link || 'No link provided'}
                                    </div>
                                </div>
                                <Icon
                                    className="externalLinksPreviewContentShareIcon"
                                    size={24}
                                    icon="externalLink"
                                    style={{
                                        marginLeft: 'auto',
                                        cursor: 'pointer',
                                    }}
                                    color={textColor}
                                    onClick={() => {
                                        window.open(platform.link, '_blank');
                                    }}
                                />
                            </div>
                        )
                    case ExternalLinksStyle.STACKED_SQUARES:
                        return (
                            <div
                                key={makeKey(platform.name, index, contentIndex)}
                                className="externalLinksPreviewStackedSquares"
                                style={styx(
                                    sty.externalLinksPreviewStackedSquares,
                                    {
                                        backgroundColor: secondaryColorLocal,
                                        fontFamily,
                                        color: textColor
                                    }
                                )}
                                onClick={() => {
                                    window.open(platform.link, '_blank');
                                }}
                            >
                                {platform.title}
                            </div>
                        )
                    case ExternalLinksStyle.STACKED_ROUNDED:
                        return (
                            <div
                                key={makeKey(platform.name, index, contentIndex)}
                                className="externalLinksPreviewStackedRounded"
                                style={styx(
                                    sty.externalLinksPreviewStackedRounded,
                                    {
                                        backgroundColor: secondaryColorLocal,
                                        fontFamily,
                                        color: textColor
                                    }
                                )}
                                onClick={() => {
                                    window.open(platform.link, '_blank');
                                }}
                            >
                                {platform.title}
                            </div>
                        )
                    case ExternalLinksStyle.BOXES:
                        return (
                            <div
                                key={makeKey(platform.name, index, contentIndex)}
                                className="externalLinksPreviewBoxes"
                                style={styx(
                                    sty.externalLinksPreviewBoxes,
                                    {
                                        backgroundColor: secondaryColorLocal,
                                        fontFamily,
                                        color: textColor
                                    }
                                )}
                                onClick={() => {
                                    window.open(platform.link, '_blank');
                                }}
                            >
                                {platform.title}
                            </div>
                        )
                }
            })}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    externalLinksPreviewContentComponent: {
        display: 'flex',
        flexWrap: 'wrap',
        width: '100%',
        justifyContent: 'center',
        gap: 8,
        paddingLeft: 16,
        paddingRight: 16,
    },
    externalLinksPreviewContentPlatform: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: 8,
        padding: 8,
        borderRadius: 8,
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderStyle: 'solid',
    },
    externalLinksPreviewBoxes: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        width: 'calc(50% - 4px)',
        borderRadius: 8,
        padding: 16,
        flexShrink: 0,
        cursor: 'pointer',
        gap: 8,
    },
    externalLinksPreviewStackedSquares: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        flex: 1,
        padding: 12,
    },
    externalLinksPreviewStackedRounded: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        flex: 1,
        padding: 12,
        borderRadius: 100,
    },
}

export default ExternalLinksPreviewContent;